import { View, KeyboardAvoidingView, TouchableOpacity, ScrollView, Pressable, BackHandler, useWindowDimensions, VirtualizedList, Keyboard, RefreshControl, ActivityIndicator, Image, Platform } from 'react-native'
import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ThemeConsumer } from '@rneui/themed'
import { FormikErrors, useFormik } from 'formik'
import * as Yup from 'yup'
import Popover, { PopoverPlacement } from 'react-native-popover-view'

import { config } from '../../../shared/environment'

import CommonHeader from '../../../shared/commonComponents/commonHeader'
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox'
import CommonImageUpload, { commonPickerRes, ZenRBSheetRef } from '../../../shared/commonComponents/commonImageUpload';
import { ListToolbar } from '../../../shared/commonComponents/listToolbar'

import { ZenBadge, ZenEmptyComponent, ZenIcon, ZenInput, ZenLoader, ZenSearchBar, ZenSwitch, ZenText, ZenToast, ZenToolTip } from 'zen-core-elements'
import { appColors } from '../../../themes/appTheme'

import { useAppDispatch, useAppSelector } from '../../../redux/hooks'
import { useAppNavigation } from '../../../types/@react-navigation/navigation'
import { bulkEditFormikType, bulkEditRenderItemType, bulkEditVirtualizedListType, bulkProductImageType, createImageType, formikStatus, updateBulkEditProductDetailsPayload } from './productModule'
import { Code } from '../../customers/addEditCustomerModule'
import { CheckDuplicatesProps } from '../../customerGroups/customerGroupModule'

import { getPosSortCategoryList } from '../../../redux/slices/categorySlice'
import { getBulkProductDetails, updateBulkEditProductDetails } from '../../../redux/slices/productSlice'
import { checkDuplicatesRedux, pageDirty } from '../../../redux/slices/commonSlice'
import { CustomModal } from '../../../shared/commonComponents/customModal'

const allCategory: Code = { label: "All", value: "" }
//Maximum images allowed for a variant, same as the add/edit product variant screen
const maxVariantImages = 5

//Method to check whether a single row differs from its originally fetched value
const isRowEdited = (row: any, original: any) => {
	if (!row || !original) return false
	return Object.keys(row).some((key) => {
		if (key == "updateImages") {
			return (row[key]?.isDeleted?.length != original[key]?.isDeleted?.length)
				|| (row[key]?.isCreate?.length != original[key]?.isCreate?.length)
		}
		return row[key] != original[key]
	})
}

let limit = 10
const BulkEdit = () => {
	const navigation = useAppNavigation()
	const dispatch = useAppDispatch()
	const { width } = useWindowDimensions()

	const [offset, setOffset] = useState<number>()
	const [isDirty, setIsDirty] = useState(false)
	const [totalCount, setTotalCount] = useState(0)
	const [listLoader, setListLoader] = useState(true)
	const [pageLoader, setPageLoader] = useState(true)
	const [onEndReachedLoader, setOnEndReachedLoader] = useState(false)
	const [isRefresh, setIsRefresh] = useState(false)
	const [transparentLoader, setTransparentLoader] = useState(false)
	const [search, setSearch] = useState<null | string>(null)
	const { id: locationId, name: locationName } = useAppSelector((state) => state?.common?.currentLocation)
	const [categorySearch, setCategorySearch] = useState("")
	const [categoryOpen, setCategoryOpen] = useState(false)
	const [categoryValue, setCategoryValue] = useState<Code>({ label: "", value: "" })
	const [categoryData, setCategoryData] = useState<Code[]>([])

	const [skuValidationErrors, setSkuValidationErrors] = useState<{ [key: string]: string }>({});
	const [productNameValidationErrors, setProductNameValidationErrors] = useState<{ [key: string]: string }>({});


	//variable to handle leave popover
	const [leavePopoverVisible, setLeavePopoverVisible] = useState(false)
	const [confirmationPopover, setConfirmationPopover] = useState<{ visible: boolean, value?: Code }>({ visible: false })
	const [dialogTitle, setDialogTitle] = useState({
		content: "",
		button: [{ id: 1, name: "cancel" }, { id: 2, name: "okay" }],
		icon: { name: 'alert-outline', size: 55, color: appColors.warning, type: 'material-community' }
	})

	const [formikDetails, setFormikDetails] = useState<bulkEditFormikType["details"]>([])
	const [vendorName, setVendorName] = useState<Code[]>([])

	const yupValidation = Yup.object().shape({
		details: Yup.array().of(
			Yup.object().shape({
				productName: Yup.string().max(250, "The product name should not exceed 255 characters"),
				shortDescription: Yup.string().max(250, "The description should not exceed 250 characters"),
				sellingPrice: Yup.number().required("The selling price is required")
					.moreThan(0.01, "The selling price must be greater than or equal to 0.01")
					.lessThan(1000000, "The selling price should not exceed 10,00,000"),
				actualPrice: Yup.number()
					.moreThan(0.01, "TThe cost price must be greater than or equal to 0.01")
					.lessThan(1000000, "The cost price should not exceed 10,00,000"),
				wholeSalePrice: Yup.number()
					.lessThan(1000000, "The wholeSale price should not exceed 10,00,000")
					.when("sellingPrice", (sellingPrice, schema) => {
						return (
							schema?.test(
								"greater-or-equal",
								"WholeSale price should be less than or equal to the selling price",
								(wholeSalePrice) => {
									if (wholeSalePrice == undefined || wholeSalePrice?.toString() == "") {
										return true
									}
									else if (wholeSalePrice <= sellingPrice[0]) {
										return true
									} else {
										return false
									}
								})
						)
					}),
				MRP: Yup.number().lessThan(1000000, "The MRP should not exceed 10,00,000")
					.when("sellingPrice", (sellingPrice, schema) => {
						return (
							schema?.test(
								"greater-or-equal",
								"MRP should be greater than or equal to the selling price",
								(MRP) => {
									if (MRP == undefined || MRP?.toString() == "") {
										return true
									}
									else if (MRP >= sellingPrice[0]) {
										return true
									} else {
										return false
									}
								})
						)
					}),
				stock: Yup.string().required("Stock in Hand is Required"),
				lowStock: Yup.string().required("Low Stock is Required"),
				sku: Yup.string().max(64, "SKU should not exceed 64 characters")
			})
		)
	})
	const checkDuplicateInArray = ({ index, value, type }: { value: string, index: number, type: "sku" | "productName" }) => {
		return formik?.values?.details?.every((item, eachIndex) => {
			if (index === eachIndex || value === "") {
				return true
			} else {
				if (item?.[type] !== value) {
					return true
				} else {
					return false
				}
			}
		})
	}
	const formik = useFormik({
		initialValues: { details: [] },
		validationSchema: yupValidation,
		onSubmit: (value: bulkEditFormikType) => {
			setTransparentLoader(true)
			let error = false
			let ids = findDirtyObject({ form: value?.details, original: formikDetails, isDirtyOnly: false }) as Map<number, number>
			ids?.forEach((item) => {
				let sku = value?.details?.[item]?.sku
				let skuErrors = skuValidationErrors[item]
				if (!checkDuplicateInArray({ value: sku, index: item, type: "sku" }) || skuErrors) {
					formik?.setFieldError(`details[${item}].sku`, 'This sku value already in use')
					error = true
				}
				let productName = value?.details?.[item]?.productName
				let productNameErrors = productNameValidationErrors[item]
				if (!checkDuplicateInArray({ value: productName, index: item, type: "productName" }) || productNameErrors) {
					formik?.setFieldError(`details[${item}].productName`, 'This product name already in use')
					error = true
				}
				if (!value?.details?.[item]?.actualPrice) {
					formik.setFieldError(`details[${item}].actualPrice`, 'The Cost Price is required')
					error = true
				}
				if (!value?.details?.[item]?.productName) {
					formik.setFieldError(`details[${item}].productName`, 'The Product Name is required')
					error = true
				}
			})
			if (ids?.size && !error) {
				updateProductsDetails(ids)
				setTransparentLoader(false)
			} else {
				if (error) {
					ZenToast({ type: "warning", message: "Please fill the required fields" })
				} else
					ZenToast({ type: "warning", message: "You have no changes to update" })
				setTransparentLoader(false)
			}
		}
	})
	const formikTypedStatus = formik.status as formikStatus
	const formikTypedSetStatus = formik.setStatus as (status: formikStatus) => void
	const formikErrors = formik?.errors?.details as FormikErrors<bulkEditFormikType["details"]>

	//Indexes of the rows which have unsaved changes
	const editedIndexes = useMemo(() => {
		let indexes = new Set<number>()
		formik?.values?.details?.forEach((row, index) => {
			if (isRowEdited(row, formikDetails?.[index])) {
				indexes.add(index)
			}
		})
		return indexes
	}, [formik?.values?.details, formikDetails])

	//UseEffect for handle Hardware BackPress
	useEffect(() => {
		dispatch(pageDirty(isDirty))
		const backHandler = BackHandler.addEventListener('hardwareBackPress', () => {
			if (isDirty) {
				setLeavePopoverVisible(true)
				return true
			}
			return false
		});
		return () => backHandler?.remove()
	}, [isDirty])

	useEffect(() => {
		setPageLoader(true)
		fetchCategory()
	}, [])
	const updateProductsDetails = async (ids: Map<number, number>) => {
		let editedProduct: updateBulkEditProductDetailsPayload["ProductsArray"] = []
		formik?.values?.details?.map((product) => {
			if (ids?.has(product?.varientId)) {
				// Sort order follows the order the images are shown: existing images first, then the newly uploaded ones
				const variantImages = [...(product?.productImage || []), ...(product?.updateImages?.isCreate || [])]
					.map((image, imageIndex) => ({ ...image, sortOrder: imageIndex + 1 }))
				const createdImages = variantImages.slice(product?.productImage?.length || 0) as createImageType[]
				editedProduct?.push({
					id: product?.varientId,
					variantId: product?.id,
					name: product?.productName,
					variantName: product?.varientName?.length > 250 ? product?.varientName?.slice(0, 250) : product?.varientName,
					price: product?.sellingPrice?.toString(),
					cost: product?.actualPrice?.toString(),
					costComparePrice: product?.MRP?.toString() || null,
					isPos: true,
					imagelength: variantImages.length,
					isImageSort: true,
					isDefault: product?.isDefaultVarient,
					locations: [{
						id: product?.locationId,
						stock: Number(product?.stock),
						lowStockThresholdValue: product?.lowStock,
						isUnlimitedStock: false,
						isUnLimited: "limitedStock"
					}],
					preferredVendorId: product?.preferredVendorId == -1 ? null : product?.preferredVendorId,
					productAvailability: product?.isHideFromStore,
					shortDescription: product?.shortDescription,
					wholeSalePrice: product?.wholeSalePrice?.toString() || null,
					variantImages: variantImages,
					updateImages: {
						isDeleted: product?.updateImages?.isDeleted || [],
						isCreate: createdImages
					},
					sku: product?.sku || null,
					isVariants: product?.isVariants,
					height: product?.height,
					isDigitalProduct: product?.isDigitalProduct,
					isShippingEnabled: product?.isShippingEnabled,
					length: product?.length,
					scheduleProduct: product?.scheduleProduct,
					weight: product?.weight,
					width: product?.width,
					variantAvailability: product?.isHideFromStore
				})
			}
		})
		await dispatch(updateBulkEditProductDetails({
			payload: {
				ProductsArray: editedProduct
			}
		})).unwrap().then((response) => {
			setTransparentLoader(false)
			if (response?.success) {
				dispatch(pageDirty(false))
				ZenToast({ type: "success", message: "Products updated successfully" })
				navigation?.goBack()
			}
		}).catch((error) => {
			console.log("Update product error", error)
			ZenToast({ type: "error", message: "Failed to update the product details" })
			setTransparentLoader(false)
		})
	}

	const fetchCategory = async () => {
		await dispatch(getPosSortCategoryList({ storeId: config?.storeId, data: { sort: true, isProductPage: true } })).unwrap().then((response) => {
			if (response) {
				setCategoryData([{ name: "All", id: "" }, ...response?.category]?.map((list) => ({
					label: list?.name,
					value: list?.id
				})))
			}
		}).catch((error) => {
			console.log("Fetch category error", error)
			ZenToast({ message: "Failed to get the category list", type: "error" })
		}).finally(() => {
			// Products are loaded for "All" categories even if the category list fails
			setCategoryValue({ ...allCategory })
			setPageLoader(false)
		})
	}
	const fetchBulkProductDetails = async () => {
		await dispatch(getBulkProductDetails({
			payload: {
				isPos: true,
				limit,
				offset: offset == -1 ? 0 : offset!!,
				locationId: locationId,
				locationName: locationName,
				categoryId: categoryValue?.value,
				...search && { searchText: search }
			}
		})).unwrap().then((response) => {
			if (response) {
				let data: bulkEditFormikType["details"] = []
				setTotalCount(response?.products?.count)
				if (response?.products?.rows?.length) {
					data = response?.products?.rows?.map((value) => ({
						id: value?.id,
						varientId: value?.product?.id,
						productName: value?.product?.name,
						varientName: value?.variantName,
						shortDescription: value?.product?.shortDescription || "",
						preferredVendorId: value?.preferredVendorId || -1,
						actualPrice: Number(value?.cost) || "",
						sellingPrice: Number(value?.price) || 0,
						MRP: Number(value?.costComparePrice) || "",
						wholeSalePrice: Number(value?.wholeSalePrice) || "",
						sku: value?.sku || "",
						isDefaultVarient: value?.isDefault,
						isHideFromStore: value?.variantAvailability,
						isLimitedStock: value?.multiLocationVariantStocks?.[0]?.stock !== -1 ? true : false,
						stock: value?.multiLocationVariantStocks?.[0]?.stock ?? "",
						productImage: Object?.keys(value?.images)?.length ? (value?.images as bulkProductImageType)?.rows : [],
						updateImages: { isCreate: [], isDeleted: [] },
						isEdited: false,
						lowStock: value?.multiLocationVariantStocks?.[0]?.lowStockThresholdValue ?? 0,
						height: value?.height,
						isDigitalProduct: value?.product?.isDigitalProduct,
						isShippingEnabled: value?.product?.isShippingEnabled,
						isVariants: value?.product?.isVariants,
						length: value?.length,
						locationId: value?.multiLocationVariantStocks?.[0]?.id,
						scheduleProduct: value?.product?.scheduleProduct,
						weight: value?.weight,
						width: value?.width
					}))
				}
				if (offset!! <= 0) {
					setFormikDetails([...data])
					formik?.setFieldValue("details", [...data])
				}
				else {
					if (response?.products?.rows?.length) {
						let newData = [...formik?.values?.details, ...data]
						setFormikDetails((prev) => ([...prev, ...data]))
						formik?.setFieldValue("details", [...newData])
					}
				}
			}
		}).catch((error) => {
			console.log("Fetch bulk product error", error)
			ZenToast({ message: "Failed to get the product details", type: "error" })
		}).finally(() => {
			setOnEndReachedLoader(false)
			setIsRefresh(false)
			setListLoader(false)
		})
	}

	useEffect(() => {
		// label is empty only before the category list has been loaded
		if (categoryValue?.label) {
			toggleOffsetValue()
		}
	}, [categoryValue])

	useEffect(() => {
		if (offset != undefined) {
			fetchBulkProductDetails()
		}
	}, [offset])

	useEffect(() => {
		setIsDirty(editedIndexes.size > 0)
	}, [editedIndexes])

	useEffect(() => {
		if (search?.length) {
			const debounceSearchText = setTimeout(() => {
				let isNotDirty = checkDirty({ activity: "search" })
				if (isNotDirty) {
					setListLoader(true)
					toggleOffsetValue()
				}
			}, 1000);
			return () => clearTimeout(debounceSearchText)
		} else if (search == "") {
			let isNotDirty = checkDirty({ activity: "search" })
			if (isNotDirty) {
				setListLoader(true)
				toggleOffsetValue()
			}
		}
	}, [search])

	const toggleOffsetValue = () => {
		setOffset((prev) => prev == -1 ? 0 : -1)
	}

	const searchedCategoryData = useMemo(() => (
		categoryData?.filter((item) => (
			categorySearch ? item?.label?.toLowerCase().includes(categorySearch?.toLowerCase()) : item
		))
	), [categorySearch, categoryData])

	const checkDirty = ({ activity, value }: { activity: "refresh" | "category" | "search" | "discard", value?: Code }): boolean => {
		if (isDirty) {
			let title = 'You have unsaved changes '
			switch (activity) {
				case "category": {
					title = 'You have unsaved changes. Are you sure you want to change the category?'
					setDialogTitle({
						content: title,
						button: [{ id: 3, name: "Okay" }, { id: 4, name: "Cancel" }],
						icon: dialogTitle?.icon
					})
					break;
				}
				case "refresh": {
					title = 'You have unsaved changes. Are you sure you want to refresh this page?'
					setDialogTitle({
						content: title,
						button: [{ id: 1, name: "Okay" }, { id: 2, name: "Cancel" }],
						icon: dialogTitle?.icon
					})
					break;
				}
				case "search": {
					title = "You have unsaved changes. Are you sure you want to continue the search?"
					setDialogTitle({
						content: title,
						button: [{ id: 7, name: "Okay" }, { id: 8, name: "Cancel" }],
						icon: dialogTitle?.icon
					})
					break
				}
				case "discard": {
					title = "Are you sure you want to discard all unsaved changes?"
					setDialogTitle({
						content: title,
						button: [{ id: 9, name: "Discard" }, { id: 10, name: "Cancel" }],
						icon: dialogTitle?.icon
					})
					break
				}
			}
			setConfirmationPopover({ value, visible: true })
			return false
		}
		return true
	}
	const findDirtyObject = ({ form, isDirtyOnly, original }: { form: any, original: any, isDirtyOnly: boolean }) => {
		let dirtyArr: Map<number, number> = new Map([])
		if (form?.length && original?.length) {
			for (let mainIndex in form) {
				if (isRowEdited(form?.[mainIndex], original?.[mainIndex])) {
					dirtyArr.set(form?.[mainIndex]?.["varientId"], Number(mainIndex))
					if (isDirtyOnly) {
						return true
					}
				}
			}
		}
		if (isDirtyOnly) {
			return false
		}
		return dirtyArr
	}
	//Method to restore every row to the last fetched values
	const discardChanges = () => {
		formik?.setErrors({})
		formik?.setTouched({})
		formik?.setFieldValue("details", [...formikDetails], false)
		formikTypedSetStatus({})
		setSkuValidationErrors({})
		setProductNameValidationErrors({})
	}
	const confirmation = (item: { id: number }) => {
		setLeavePopoverVisible(false)
		if (item?.id == 2) {
			navigation?.goBack()
			dispatch(pageDirty(false))
		}
	}
	const activityConfirmation = (item: { id: number }) => {
		setConfirmationPopover((prev) => ({ ...prev, visible: false }))
		switch (item?.id) {
			case 1: {
				setIsRefresh(true)
				toggleOffsetValue()
				break;
			}
			case 3: {
				setListLoader(true)
				setCategoryValue(confirmationPopover?.value!!)
				break;
			}
			case 7: {
				setListLoader(true)
				toggleOffsetValue()
				break;
			}
			case 8: {
				setSearch(null)
				break
			}
			case 9: {
				discardChanges()
				break
			}
		}
	}
	//Method to change the category, asking for confirmation when there are unsaved changes
	const handleCategoryChange = (category: Code) => {
		setCategoryOpen(false)
		if (category?.value === categoryValue?.value) return
		if (!isDirty) {
			setListLoader(true)
			setCategoryValue(category)
		} else {
			// Wait for the category popover to close before opening the confirmation popover
			setTimeout(() => {
				checkDirty({ activity: "category", value: category })
			}, 600);
		}
	}
	const handleRefresh = useCallback(() => {
		let isRefresh = checkDirty({ activity: "refresh" })
		if (isRefresh) {
			setSearch(null)
			setIsRefresh(true)
			toggleOffsetValue()
		}
	}, [isDirty])
	const handleOnEndReached = useCallback(() => {
		let currentOffset = offset == -1 ? 0 : offset!!
		let newoffset = currentOffset + limit
		if (totalCount > newoffset && !onEndReachedLoader) {
			setOnEndReachedLoader(true)
			setOffset(newoffset)
		}
	}, [offset, totalCount, onEndReachedLoader])

	const checkDuplicateSku = ({ index, text, id }: { text: string, index: number, id: number }) => {
		formikTypedSetStatus({ sku: { ...formikTypedStatus?.sku!!, loading: true } })
		const data: CheckDuplicatesProps = {
			"value": text,
			"tableName": "variantCombinationDetails",
			"columnName": "sku",
			"id": id,
			"storeId": true,
			"isForCustomer": null,
			"includeTable": "products"
		}
		dispatch(checkDuplicatesRedux({ storeId: config?.storeId, data: data })).unwrap().then((response) => {
			if (response?.result?.alreadyExists) {
				setSkuValidationErrors((prev) => ({
					...prev,
					[index]: "This sku value already in use"
				}))
				formik?.setFieldError(`details[${index}].sku`, "This sku value already in use")
			} else {
				setSkuValidationErrors((prev) => {
					let errors = { ...prev }
					delete errors[index]
					return errors
				})
				formik?.setFieldError(`details[${index}].sku`, undefined)
			}

		}).catch((error: any) => {
			ZenToast({ type: "error", message: "Failed to check the duplicate sku value" })
			console.log("check duplicates sku", error)
		}).finally(() => {
			formikTypedSetStatus({ sku: { index: -1, loading: false, id: -1, value: "" } })
		})
	}

	useEffect(() => {
		if (formikTypedStatus?.sku && formikTypedStatus?.sku?.id !== -1) {
			let { value = "", index = -1, id = 0 } = formikTypedStatus?.sku
			let debounceText = setTimeout(() => {
				if (!formikErrors?.[index]?.sku && value !== "") {
					checkDuplicateSku({ text: value, index, id });
				} else {
					setSkuValidationErrors((prev) => {
						let errors = { ...prev }
						delete errors[index]
						return errors
					})
					formik?.setFieldError(`details[${index}].sku`, undefined)
					formikTypedSetStatus({ sku: { index: -1, loading: false, id: -1, value: "" } })
				}
			}, 1000);
			return () => {
				clearTimeout(debounceText)
			}
		}
	}, [formikTypedStatus?.sku])

	const checkDuplicateProductName = ({ index, text, id }: { text: string, index: number, id: number }) => {
		formikTypedSetStatus({ productName: { ...formikTypedStatus?.productName!!, loading: true } })
		const data: CheckDuplicatesProps = {
			"value": text,
			"tableName": "products",
			"columnName": "name",
			"id": id,
			"storeId": true
		}
		dispatch(checkDuplicatesRedux({ storeId: config?.storeId, data: data })).unwrap().then((response) => {
			if (response?.result?.alreadyExists) {
				setProductNameValidationErrors((prev) => ({
					...prev,
					[index]: "This product name already in use"
				}))
				formik?.setFieldError(`details[${index}].productName`, "This product name already in use")
			} else {
				setProductNameValidationErrors((prev) => {
					let errors = { ...prev }
					delete errors[index]
					return errors
				})
				formik?.setFieldError(`details[${index}].productName`, undefined)
			}

		}).catch((error: any) => {
			ZenToast({ type: "error", message: "Failed to check the duplicate product name value" })
			console.log("check duplicates productName", error)
		}).finally(() => {
			formikTypedSetStatus({ productName: { index: -1, loading: false, id: -1, value: "" } })
		})
	}

	useEffect(() => {
		if (formikTypedStatus?.productName && formikTypedStatus?.productName?.id !== -1) {
			let { value = "", index = -1, id = 0 } = formikTypedStatus?.productName
			let debounceText = setTimeout(() => {
				if (!formikErrors?.[index]?.productName && value !== "" && formikDetails?.[index]?.productName.toLowerCase() !== value.toLowerCase()) {
					checkDuplicateProductName({ text: value, index, id });
				} else {
					setProductNameValidationErrors((prev) => {
						let errors = { ...prev }
						delete errors[index]
						return errors
					})
					formik?.setFieldError(`details[${index}].productName`, undefined)
					formikTypedSetStatus({ sku: { index: -1, loading: false, id: -1, value: "" } })
				}
			}, 1000);
			return () => {
				clearTimeout(debounceText)
			}
		}
	}, [formikTypedStatus?.productName, formikDetails])

	const isChecking = formikTypedStatus?.sku?.loading || formikTypedStatus?.productName?.loading
	const isCategoryFiltered = !!categoryValue?.value

	return (
		<ThemeConsumer>
			{({ theme }) => (
				<KeyboardAvoidingView behavior={Platform.OS === "ios" ? "padding" : undefined} style={theme?.bulkEditStyle?.containerView}>
					<CommonHeader
						headerName='Bulk Edit Products'
						backIcon
						backIconOnpress={() => { isDirty ? setLeavePopoverVisible(true) : navigation?.goBack() }}
					/>
					<ListToolbar
						search={{
							value: search,
							onChange: (text) => setSearch(text as string),
							placeholder: 'Search by product name'
						}}
						filter={{
							count: isCategoryFiltered ? 1 : 0,
							onPress: () => { setCategorySearch(""); setCategoryOpen(true) }
						}}
						forceAddButton
						buttons={[
							{
								title: 'Discard',
								variant: 'outline',
								disabled: !isDirty || isChecking,
								onPress: () => checkDirty({ activity: "discard" })
							},
							{
								title: editedIndexes.size ? `Save (${editedIndexes.size})` : 'Save',
								disabled: !isDirty || isChecking,
								onPress: () => {
									Keyboard.dismiss()
									formik?.handleSubmit()
									if (!formik?.isValid) {
										ZenToast({ type: "warning", message: "Please fill the required fields" })
									}
								}
							}
						]}
					/>
					{pageLoader ?
						<ZenLoader viewStyle={theme?.bulkEditStyle?.loaderView} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} /> :
						<View style={{ flex: 1 }} onStartShouldSetResponder={() => { Keyboard?.dismiss(); return false }}>
							{/* Summary: product count, unsaved changes, active category & location */}
							<View style={theme?.bulkEditStyle?.summaryBar}>
								<View style={theme?.bulkEditStyle?.summaryLeft}>
									<ZenText
										content={listLoader ? "Loading products..." : `Showing ${formik?.values?.details?.length || 0} of ${totalCount || 0} products`}
										size={theme?.smallerFontSize}
										color={theme?.labelColor}
									/>
									{isDirty ?
										<View style={theme?.bulkEditStyle?.unsavedChip}>
											<ZenIcon type="material-community" name="pencil-outline" size={13} color={theme?.goldenOrange} />
											<ZenText content={`${editedIndexes.size} unsaved`} size={theme?.badgeFontSize} color={theme?.lightDark} bold />
										</View> : <></>}
								</View>
								<View style={theme?.bulkEditStyle?.summaryRight}>
									{isCategoryFiltered ?
										<TouchableOpacity
											style={theme?.bulkEditStyle?.chip}
											onPress={() => handleCategoryChange({ ...allCategory })}
											accessibilityLabel="Clear category filter">
											<ZenText content={categoryValue?.label} size={theme?.tinyFontSize} color={theme?.primary} bold numberOfLines={1} style={{ maxWidth: 150 }} />
											<ZenIcon type="material-community" name="close-circle" size={15} color={theme?.primary} />
										</TouchableOpacity> : <></>}
									{locationName ?
										<View style={theme?.bulkEditStyle?.chip}>
											<ZenIcon type="material-community" name="map-marker-outline" size={15} color={theme?.primary} />
											<ZenText content={locationName} size={theme?.tinyFontSize} color={theme?.primary} bold numberOfLines={1} style={{ maxWidth: 150 }} />
										</View> : <></>}
								</View>
							</View>
							{listLoader ?
								<ZenLoader viewStyle={theme?.bulkEditStyle?.loaderView} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} /> :
								<BulkEditVirtualizedList
									touched={formik.touched}
									errors={formik?.errors}
									setFieldValue={formik.setFieldValue}
									setFieldTouched={formik?.setFieldTouched}
									vendorName={vendorName}
									formikData={formik?.values?.details}
									onEndReachedLoader={onEndReachedLoader}
									handleOnEndReached={handleOnEndReached}
									handleRefresh={handleRefresh}
									isRefresh={isRefresh}
									status={formikTypedStatus}
									setStatus={formikTypedSetStatus}
									editedIndexes={editedIndexes}
								/>
							}
						</View>}
					{/* Category filter */}
					<Popover
						isVisible={categoryOpen}
						onRequestClose={() => { setCategoryOpen(false) }}
						popoverStyle={{ width: width > 600 ? 400 : 320, borderRadius: 12 }}
					>
						<View style={theme?.bulkEditStyle?.categoryPopover}>
							<View style={theme?.bulkEditStyle?.categoryPopoverHeader}>
								<ZenText content="Filter by category" size={theme?.mediumFontSize} color={theme?.primary} bold />
								<TouchableOpacity onPress={() => setCategoryOpen(false)} accessibilityLabel="Close">
									<ZenIcon type="material-community" name="close" size={22} color={theme?.lightDark} />
								</TouchableOpacity>
							</View>
							<ZenSearchBar
								placeholder='Search category'
								searchInputStyle={{ fontFamily: theme.medium, fontSize: theme.smallerFontSize }}
								search={categorySearch}
								setSearch={setCategorySearch}
							/>
							<ScrollView contentContainerStyle={{ flexGrow: 1 }} keyboardShouldPersistTaps="handled">
								{searchedCategoryData?.length ? searchedCategoryData?.map((category, index) => {
									const isSelected = categoryValue?.value === category?.value
									return (
										<TouchableOpacity key={index}
											onPress={() => handleCategoryChange(category)}
											style={[theme?.bulkEditStyle?.categoryItem, isSelected && theme?.bulkEditStyle?.categoryItemSelected]}>
											<ZenText content={category?.label} color={isSelected ? theme?.primary : theme.lightDark} bold={isSelected} size={theme?.smallerFontSize} />
											{isSelected ? <ZenIcon size={16} name="check" type="entypo" color={theme.primary} /> : <></>}
										</TouchableOpacity>
									)
								}) :
									<View style={theme?.bulkEditStyle?.emptyCategory}>
										<ZenText content="No Category Found" size={theme?.smallerFontSize} color={theme?.lightDark} />
									</View>
								}
							</ScrollView>
						</View>
					</Popover>
					<CustomModal visible={transparentLoader} transparent>
						<ZenLoader viewStyle={{ backgroundColor: "transparent" }} color={theme?.primary} lottieType="Local" lottie={require('../../../assets/infinityLoader.json')} />
					</CustomModal>
					{/* For leave confirmation */}
					<Popover isVisible={leavePopoverVisible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }}>
						<CommonDialogBox type={"EDIT"} replaceValue="page" confirmation={confirmation} />
					</Popover>
					<Popover isVisible={confirmationPopover?.visible} popoverStyle={{ width: width > 600 ? 400 : 300, borderRadius: 10 }} onRequestClose={() => setConfirmationPopover({ visible: false })}>
						<CommonDialogBox dialog={dialogTitle} type='OTHER' confirmation={activityConfirmation} />
					</Popover>
				</KeyboardAvoidingView>
			)}
		</ThemeConsumer>
	)
}

//Section heading used inside the product card
const SectionTitle = ({ icon, title }: { icon: string, title: string }) => (
	<ThemeConsumer>
		{({ theme }) => (
			<View style={[theme?.bulkEditStyle?.sectionTitle, { width: "100%" }]}>
				<ZenIcon type="material-community" name={icon} size={16} color={theme?.primary} />
				<ZenText content={title} size={theme?.smallerFontSize} color={theme?.primary} bold />
			</View>
		)}
	</ThemeConsumer>
)

//Info icon with a tooltip, shown on the right of an input
const InfoToolTip = ({ content }: { content: string }) => (
	<ThemeConsumer>
		{({ theme }) => (
			<ZenToolTip
				content={
					<ZenText
						content={content}
						size={theme?.badgeFontSize}
						color={theme?.light}
					/>
				}
				height={100}
				popover={
					<Pressable hitSlop={8}>
						<ZenIcon
							name="exclamationcircle"
							type="antdesign"
							size={14}
							color={theme?.primary}
						/>
					</Pressable>
				}
				width={150}
				placement={PopoverPlacement?.LEFT}
				backgroundColor={theme?.lightBackgroundColor}
				popoverStyle={{ elevation: 4, width: 250, padding: 5, backgroundColor: theme?.grey, right: 10 }}
			/>
		)}
	</ThemeConsumer>
)

export const BulkEditRenderItem = memo((
	{
		item,
		index,
		setFieldTouched,
		setFieldValue,
		errors,
		touched,
		status,
		setStatus,
		isEdited
	}: bulkEditRenderItemType
) => {
	const { width } = useWindowDimensions()
	const currencySymbol = useAppSelector((state) => state.common.currentUser?.value?.purchasedCurrency?.currencySymbol)
	const logoRef = useRef<ZenRBSheetRef>(null)
	const [isExpanded, setIsExpanded] = useState(false)

	const inputWidth = width > 600 ? "50%" : "100%"
	const formikErrors = errors?.details as FormikErrors<bulkEditFormikType["details"]>;
	const itemErrors = (formikErrors?.[index] || {}) as FormikErrors<bulkEditFormikType["details"][0]>
	const errorCount = Object.keys(itemErrors)?.length
	const allImages = [...(item?.productImage || []), ...(item?.updateImages?.isCreate || [])] as any[]
	const remainingImages = Math.max(maxVariantImages - allImages.length, 0)
	const isLowStock = item?.stock !== "" && Number(item?.stock) <= Number(item?.lowStock)
	const showVariantName = item?.isVariants && item?.varientName && item?.varientName !== item?.productName

	//Expand the card automatically when it has validation errors so they are visible
	useEffect(() => {
		if (errorCount) {
			setIsExpanded(true)
		}
	}, [errorCount])

	const handleOnChangeText = ({ formikKey, textValue }: { textValue: string, formikKey: string }) => {
		let numberValue = Number(textValue)
		if (numberValue || textValue == "") {
			if ((/^\d*\.?\d{0,2}$/).test(textValue)) {
				setFieldValue(formikKey, textValue)
			}
		}
	}
	const handleDeleteImage = (img: any, imgIndex: number, isOnlineImg: boolean) => {
		if (isOnlineImg) {
			let productImages = [...item?.productImage]
			productImages.splice(imgIndex, 1)
			setFieldValue(`details[${index}].productImage`, productImages)

			let updateImages = [...item?.updateImages.isDeleted]
			updateImages.push({ id: img?.id, ext: img?.ext, fileUrl: img?.fileUrl })
			setFieldValue(`details[${index}].updateImages.isDeleted`, updateImages)
		}
		else {
			let updateImages = [...item?.updateImages?.isCreate]
			updateImages = updateImages?.filter((image) => image?.image !== img?.image)
			setFieldValue(`details[${index}].updateImages.isCreate`, updateImages)
		}
	}
	//Method to handle the images uploaded by CommonImageUpload, they are already stored in the bucket and only the key is returned
	const handleImage = (name: string, res?: commonPickerRes[]) => {
		switch (name) {
			case 'Camera':
			case 'Library':
			case 'StoreImageGallery':
			case 'ZenbasketGallery': {
				const uploadedImages = (res || [])?.filter((image) => image?.key)
				if (!uploadedImages?.length) break
				if (uploadedImages.length > remainingImages) {
					ZenToast({ type: "warning", message: `Only ${maxVariantImages} images are allowed for a variant` })
				}
				const newImages: createImageType[] = uploadedImages.slice(0, remainingImages).map((image) => {
					const key = image?.key || ""
					const keyFileName = key.split("/").pop() || ""
					// Picked files keep their original name, gallery images only have their key
					const originalName = (name === "Library" || name === "Camera") && image?.fileName ? image.fileName : keyFileName
					return {
						ext: (key.split(".").pop() || "").toLowerCase(),
						fileName: originalName.replace(/\.[^/.]+$/, ""),
						image: key,
						isImageStore: true,
						url: config?.AWSImageUrl + key,
						isNew: true,
						fileUrl: key,
						// Sort order is assigned from the image position when the products are saved
						sortOrder: null
					}
				})
				setFieldValue(`details[${index}].updateImages.isCreate`, [...(item?.updateImages?.isCreate || []), ...newImages])
				break
			}
			case 'Cancel':
				break
		}
	}

	const getImageUri = (img: any) => img?.uri ? img?.uri : config?.AWSImageUrl + img?.fileUrl

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = theme?.bulkEditStyle
				//Common props shared by every input in the card
				const inputProps = (hasError: boolean) => ({
					type: "outlined" as const,
					renderErrorMessage: false,
					labelStyle: theme?.addEditCustomerStyle?.labelStyle,
					inputStyle: theme?.modifierSetStyle?.inputStyle,
					errorStyle: styles?.errorStyle,
					containerStyle: styles?.inputContainer,
					inputContainerStyle: [styles?.inputInnerContainer, hasError && styles?.inputErrorContainer]
				})
				const currencyIcon = <ZenText content={currencySymbol || ""} size={theme?.smallFontSize} color={theme?.labelColor} bold />
				const stepper = (key: "stock" | "lowStock") => {
					const value = Number(item?.[key]) || 0
					return (
						<View style={{ flexDirection: "row", alignItems: "center", gap: 6 }}>
							<TouchableOpacity
								disabled={!value}
								accessibilityLabel="Decrease"
								style={[styles?.stepperButton, !value && styles?.stepperButtonDisabled]}
								onPress={() => setFieldValue(`details[${index}].${key}`, value - 1)}>
								<ZenIcon type="material-community" name="minus" size={16} color={value ? theme?.primary : theme?.lightGrey} />
							</TouchableOpacity>
							<TouchableOpacity
								accessibilityLabel="Increase"
								style={styles?.stepperButton}
								onPress={() => setFieldValue(`details[${index}].${key}`, value + 1)}>
								<ZenIcon type="material-community" name="plus" size={16} color={theme?.primary} />
							</TouchableOpacity>
						</View>
					)
				}
				return (
					<View style={[styles?.card, isEdited && styles?.cardEdited, errorCount ? styles?.cardError : undefined]}>
						{/* Card header: always visible summary of the product */}
						<Pressable style={styles?.cardHeader} onPress={() => setIsExpanded((prev) => !prev)}>
							<Image
								style={styles?.thumbnail}
								resizeMode="cover"
								source={allImages?.length ? { uri: getImageUri(allImages[0]) } : require("../../../assets/noimage.jpg")}
								defaultSource={require("../../../assets/noimage.jpg")}
							/>
							<View style={styles?.headerDetails}>
								<ZenText content={item?.productName || item?.varientName} size={theme?.smallFontSize} color={theme?.lightDark} bold numberOfLines={1} />
								{showVariantName ? <ZenText content={item?.varientName} size={theme?.tinyFontSize} color={theme?.labelColor} numberOfLines={1} /> : <></>}
								<View style={styles?.headerBadgeRow}>
									<View style={styles?.infoPill}>
										<ZenText content={`${currencySymbol || ""}${item?.sellingPrice || 0}`} size={theme?.badgeFontSize} color={theme?.primary} bold />
									</View>
									<View style={isLowStock ? styles?.lowStockPill : styles?.infoPill}>
										<ZenText content={`${isLowStock ? "Low stock" : "Stock"}: ${item?.stock === "" ? "-" : item?.stock}`} size={theme?.badgeFontSize} color={isLowStock ? theme?.red : theme?.primary} bold />
									</View>
									{item?.isDefaultVarient ?
										<ZenBadge
											badgeName={"Default variant"}
											badgeColor={theme?.badgeColor}
											badgeTextColor={theme?.light}
											badgeTextSize={theme?.badgeFontSize}
										/> : <></>}
									{isEdited ?
										<ZenBadge
											badgeName={"Edited"}
											badgeColor={theme?.primary}
											badgeTextColor={theme?.light}
											badgeTextSize={theme?.badgeFontSize}
										/> : <></>}
									{errorCount ?
										<ZenBadge
											badgeName={`${errorCount} ${errorCount > 1 ? "errors" : "error"}`}
											badgeColor={theme?.red}
											badgeTextColor={theme?.light}
											badgeTextSize={theme?.badgeFontSize}
										/> : <></>}
								</View>
							</View>
							<View style={styles?.headerActions}>
								<View style={styles?.availability}>
									<ZenSwitch
										activeColor={theme.primary}
										inActiveColor={theme.borderColor}
										activeThumbColor={theme.light}
										inactiveThumbColor={theme.light}
										onValueChange={() => { setFieldValue(`details[${index}].isHideFromStore`, !item?.isHideFromStore) }}
										value={item?.isHideFromStore}
									/>
									<ZenText content={item?.isHideFromStore ? "Available" : "Hidden"} size={theme?.badgeFontSize} color={item?.isHideFromStore ? theme?.primary : theme?.labelColor} />
								</View>
								<View style={styles?.expandButton}>
									<ZenIcon type="material-community" name={isExpanded ? "chevron-up" : "chevron-down"} size={22} color={theme?.primary} />
								</View>
							</View>
						</Pressable>

						{/* Card body: editable fields grouped by section */}
						<View style={[styles?.cardBody, { display: isExpanded ? "flex" : "none" }]}>
							<SectionTitle icon="information-outline" title="Basic details" />
							<View style={styles?.sectionFields}>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!itemErrors?.productName)}
										value={item?.productName}
										onChangeText={(text) => { setStatus({ productName: { index, loading: false, value: text, id: item?.id } }); setFieldValue(`details[${index}].productName`, text) }}
										label={"Product name *"}
										disabled={status?.productName?.loading}
										errorMessage={itemErrors?.productName || ''}
										rightIcon={(status?.productName?.index == index && status?.productName?.loading) ? <ActivityIndicator size={"small"} color={theme?.primary} /> : undefined}
									/>
								</View>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!itemErrors?.shortDescription)}
										value={item?.shortDescription}
										onChangeText={(text) => setFieldValue(`details[${index}].shortDescription`, text)}
										label={"Short description"}
										errorMessage={itemErrors?.shortDescription || ''}
									/>
								</View>
							</View>

							<SectionTitle icon="currency-usd" title="Pricing" />
							<View style={styles?.sectionFields}>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!(touched?.details?.[index]?.sellingPrice && itemErrors?.sellingPrice))}
										value={item?.sellingPrice?.toString()}
										onChangeText={(text) => handleOnChangeText({ formikKey: `details[${index}].sellingPrice`, textValue: text })}
										onBlur={() => setFieldTouched(`details[${index}].sellingPrice`, true)}
										label={"Selling price *"}
										keyboardType='numeric'
										leftIcon={currencyIcon}
										errorMessage={touched?.details?.[index]?.sellingPrice ? itemErrors?.sellingPrice : ''}
									/>
								</View>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!itemErrors?.MRP)}
										value={item?.MRP?.toString()}
										onChangeText={(text) => handleOnChangeText({ formikKey: `details[${index}].MRP`, textValue: text })}
										label={"MRP"}
										keyboardType='numeric'
										leftIcon={currencyIcon}
										errorMessage={itemErrors?.MRP || ''}
										rightIcon={<InfoToolTip content="To show an offer price for this product, move the Product's original price into MRP and enter a lower-price value in Selling Price" />}
									/>
								</View>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!itemErrors?.actualPrice)}
										value={item?.actualPrice?.toString()}
										onChangeText={(text) => handleOnChangeText({ formikKey: `details[${index}].actualPrice`, textValue: text })}
										label={"Cost price *"}
										keyboardType='numeric'
										leftIcon={currencyIcon}
										errorMessage={itemErrors?.actualPrice || ''}
										rightIcon={<InfoToolTip content="Cost price of the item.This will not be available to the customers" />}
									/>
								</View>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!itemErrors?.wholeSalePrice)}
										value={item?.wholeSalePrice?.toString()}
										onChangeText={(text) => handleOnChangeText({ formikKey: `details[${index}].wholeSalePrice`, textValue: text })}
										label={"Wholesale price"}
										keyboardType='numeric'
										leftIcon={currencyIcon}
										errorMessage={itemErrors?.wholeSalePrice || ''}
									/>
								</View>
							</View>

							<SectionTitle icon="package-variant-closed" title="Stock details" />
							<View style={styles?.sectionFields}>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!(touched?.details?.[index]?.stock && itemErrors?.stock))}
										value={item?.stock?.toString()}
										onChangeText={(text) => handleOnChangeText({ formikKey: `details[${index}].stock`, textValue: text })}
										onBlur={() => setFieldTouched(`details[${index}].stock`, true)}
										label={"Stock on hand *"}
										keyboardType='numeric'
										errorMessage={touched?.details?.[index]?.stock ? itemErrors?.stock : ''}
										rightIcon={stepper("stock")}
									/>
								</View>
								<View style={{ width: inputWidth }}>
									<ZenInput
										{...inputProps(!!(touched?.details?.[index]?.lowStock && itemErrors?.lowStock))}
										value={item?.lowStock?.toString()}
										onChangeText={(text) => handleOnChangeText({ formikKey: `details[${index}].lowStock`, textValue: text })}
										onBlur={() => setFieldTouched(`details[${index}].lowStock`, true)}
										label={"Low stock alert *"}
										keyboardType='numeric'
										errorMessage={touched?.details?.[index]?.lowStock ? itemErrors?.lowStock : ''}
										rightIcon={stepper("lowStock")}
									/>
								</View>
							</View>

							<SectionTitle icon="image-multiple-outline" title="Product images" />
							<View style={styles?.imageRow}>
								{remainingImages ?
									<TouchableOpacity style={styles?.addImageTile} onPress={() => { Keyboard.dismiss(); logoRef?.current?.open() }} accessibilityLabel="Add image">
										<ZenIcon type="material-community" name="camera-plus-outline" size={24} color={theme.primary} />
										<ZenText content="Add image" size={theme?.badgeFontSize} color={theme?.primary} bold />
										<ZenText content={`${allImages.length}/${maxVariantImages}`} size={theme?.badgeFontSize} color={theme?.labelColor} />
									</TouchableOpacity> : <></>}
								{item?.productImage?.map((img: any, imgIndex) => (
									<View key={`online-${imgIndex}`} style={styles?.imageTile}>
										<Image source={{ uri: getImageUri(img) }} style={styles?.image} resizeMode='cover' />
										<TouchableOpacity style={styles?.removeImageButton} onPress={() => handleDeleteImage(img, imgIndex, true)} accessibilityLabel="Remove image">
											<ZenIcon type='material-community' name='close-circle' size={22} color={theme?.red} />
										</TouchableOpacity>
									</View>
								))}
								{item?.updateImages?.isCreate?.map((img: any, imgIndex) => (
									<View key={`new-${imgIndex}`} style={styles?.imageTile}>
										<Image source={{ uri: getImageUri(img) }} style={styles?.image} resizeMode='cover' />
										<TouchableOpacity style={styles?.removeImageButton} onPress={() => handleDeleteImage(img, imgIndex, false)} accessibilityLabel="Remove image">
											<ZenIcon type='material-community' name='close-circle' size={22} color={theme?.red} />
										</TouchableOpacity>
									</View>
								))}
							</View>
						</View>
						<CommonImageUpload
							bottomRef={logoRef}
							propFn={handleImage}
							gallery={{ isStoreGallery: true, sectionName: "products", screenName: "products" }}
							selectedIndex={{ value: { imagesMaxLength: remainingImages || 1 } }}
						/>
					</View>
				)
			}}
		</ThemeConsumer >
	)
}, (prevProps, nextProps) => {
	return (prevProps.item === nextProps.item
		&& prevProps.index === nextProps.index
		&& prevProps.vendorName === nextProps.vendorName
		&& prevProps.errors === nextProps.errors
		&& prevProps.touched === nextProps.touched
		&& prevProps.setFieldTouched === nextProps.setFieldTouched
		&& prevProps.setFieldValue === nextProps.setFieldValue
		&& prevProps.status === nextProps.status
		&& prevProps.setStatus === nextProps.setStatus
		&& prevProps.isEdited === nextProps.isEdited
	)
})

export const BulkEditVirtualizedList = memo(({
	formikData,
	isRefresh,
	handleRefresh,
	handleOnEndReached,
	errors,
	touched,
	setFieldValue,
	setFieldTouched,
	onEndReachedLoader,
	vendorName,
	status,
	setStatus,
	editedIndexes
}: bulkEditVirtualizedListType) => {
	const getItem = (item: any[], index: number) => {
		return item[index];
	};

	return (
		<ThemeConsumer>
			{({ theme }) => (
				<VirtualizedList
					renderItem={({ index, item }) =>
						<BulkEditRenderItem
							errors={errors}
							touched={touched}
							setFieldValue={setFieldValue}
							setFieldTouched={setFieldTouched}
							item={item}
							index={index}
							vendorName={vendorName}
							status={status}
							setStatus={setStatus}
							isEdited={editedIndexes?.has(index)}
						/>}
					data={formikData}
					keyExtractor={(_: any, index: number) => index?.toString()}
					getItemCount={(data: any) => data?.length}
					getItem={getItem}
					contentContainerStyle={theme?.bulkEditStyle?.listContent}
					keyboardShouldPersistTaps="handled"
					refreshControl={<RefreshControl refreshing={isRefresh} onRefresh={handleRefresh} />}
					onEndReached={handleOnEndReached}
					onEndReachedThreshold={0.01}
					ListFooterComponent={onEndReachedLoader ?
						<View style={theme?.bulkEditStyle?.listFooterLoader}>
							<ActivityIndicator size={"small"} color={theme?.primary} />
						</View> : <></>}
					showsVerticalScrollIndicator={false}
					ListEmptyComponent={<ZenEmptyComponent text="No products found" viewStyle={{ backgroundColor: "transparent", flex: 1, alignItems: "center", justifyContent: "center" }} />}
				/>
			)}
		</ThemeConsumer>
	)
}, (prevProps, nextProps) => {
	return (prevProps.formikData === nextProps.formikData &&
		prevProps.isRefresh === nextProps.isRefresh &&
		prevProps.handleRefresh === nextProps.handleRefresh &&
		prevProps.handleOnEndReached === nextProps.handleOnEndReached &&
		prevProps.onEndReachedLoader === nextProps.onEndReachedLoader &&
		prevProps.setFieldTouched === nextProps.setFieldTouched &&
		prevProps.setFieldValue == nextProps.setFieldValue &&
		prevProps.vendorName === nextProps.vendorName &&
		prevProps.errors === nextProps.errors &&
		prevProps.touched === nextProps.touched &&
		prevProps.status === nextProps.status &&
		prevProps.setStatus === nextProps.setStatus &&
		prevProps.editedIndexes === nextProps.editedIndexes
	)
})

export default BulkEdit
